import React, { useState, useEffect } from 'react';
import { 
  Wallet, 
  Receipt, 
  User, 
  Phone, 
  Mail, 
  FileText, 
  Award, 
  CreditCard, 
  Hash, 
  CheckCircle2,
  Store
} from 'lucide-react';

// ============================================================
// RestaurantProfile component – reads from localStorage
// ============================================================
const RestaurantProfile = ({ isDark = false }) => {
  // ----- state for localStorage values -----
  const [ownerName, setOwnerName] = useState('Suri sahab');
  const [restaurantId, setRestaurantId] = useState('1613');
  const [userEmail, setUserEmail] = useState('suri123@gmail.com');
  const [userToken, setUserToken] = useState('authenticated');
  const [userData, setUserData] = useState({});

  // ----- load data from localStorage on mount -----
  useEffect(() => {
    const storedOwnerName = localStorage.getItem('owner_name');
    const storedRestaurantId = localStorage.getItem('restaurantId');
    const storedUserEmail = localStorage.getItem('userEmail');
    const storedUserToken = localStorage.getItem('userToken');
    const storedUserData = localStorage.getItem('userData');

    if (storedOwnerName) setOwnerName(storedOwnerName);
    if (storedRestaurantId) setRestaurantId(storedRestaurantId);
    if (storedUserEmail) setUserEmail(storedUserEmail);
    if (storedUserToken) setUserToken(storedUserToken);

    if (storedUserData) {
      try {
        setUserData(JSON.parse(storedUserData));
      } catch (e) {
        console.warn('Failed to parse userData from localStorage');
      }
    }
  }, []);

  // ----- derive display values with fallbacks -----
  const restaurantName = userData?.organisation_name || 'Suri cafe';
  const wallet = userData?.wallet || '0';
  const minOrder = userData?.min_order || '200';
  const owner = userData?.owner_name || userData?.name || ownerName;
  const phone = userData?.contact || userData?.owner_mobile || '8585858686';
  const email = userData?.email || userEmail;
  const stin = userData?.gst_number || '26AABCU4861DIZG';
  const ess = userData?.food_license_number || '21522088002357';
  const payRaw = userData?.upi || (userData?.bank_name ? 'Bank' : 'Not Set');
  const payDisplay = (payRaw && payRaw !== '' && payRaw !== 'Not Set') ? payRaw : 'Not Set';
  const accountHolder = userData?.account_holder || userData?.owner_name || 'Suri sahab';
  const isPaySet = payDisplay !== 'Not Set';

  // 🎨 ZAPTODOOR ORANGE THEME CONFIGURATION
  const theme = {
    bg: isDark ? '#0f172a' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryGradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
    accentBg: isDark ? 'rgba(249, 115, 22, 0.15)' : '#fff7ed',
    successBg: isDark ? 'rgba(22, 163, 74, 0.15)' : '#f0fdf4',
    successText: '#16a34a',
    shadow: isDark ? '0 20px 40px -12px rgba(0,0,0,0.5)' : '0 20px 40px -12px rgba(249, 115, 22, 0.15)',
  };

  // ============================================================
  // Styles – NO margins on top, left, or right
  // ============================================================
  const styles = {
    pageContainer: {
      background: theme.bg,
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0', // ← NO padding, removes all outer margins
      fontFamily: "'Inter', 'Segoe UI', Roboto, system-ui, sans-serif",
      transition: 'background-color 0.3s ease',
    },
    card: {
      maxWidth: '800px',
      width: '100%',
      background: theme.cardBg,
      borderRadius: '0', // ← flat edges, no rounded corners
      boxShadow: 'none', // ← remove shadow for edge-to-edge feel
      overflow: 'hidden',
      border: 'none', // ← no border
      transition: 'all 0.3s ease',
    },
    header: {
      padding: '2rem 2rem 1.5rem 2rem',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '1.5rem',
      borderBottom: `1px solid ${theme.border}`,
      background: isDark ? 'rgba(249, 115, 22, 0.05)' : 'rgba(255, 255, 255, 0.5)',
    },
    nameContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    restaurantName: {
      fontSize: '1.8rem',
      fontWeight: 800,
      background: theme.primaryGradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      letterSpacing: '-0.5px',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    badge: {
      background: theme.successBg,
      color: theme.successText,
      fontSize: '0.75rem',
      fontWeight: 700,
      padding: '0.35rem 1rem',
      borderRadius: '40px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      border: `1px solid ${isDark ? 'rgba(22, 163, 74, 0.3)' : '#bbf7d0'}`,
      width: 'fit-content',
    },
    walletMin: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap',
    },
    walletItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.9rem',
      fontWeight: 600,
      color: theme.text,
      background: theme.accentBg,
      padding: '0.5rem 1rem',
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
    },
    amount: {
      fontWeight: 800,
      color: theme.primary,
    },
    section: {
      padding: '1.5rem 2rem',
      borderBottom: `1px solid ${theme.border}`,
      transition: 'border-color 0.3s ease',
    },
    sectionTitle: {
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      color: theme.primary,
      fontWeight: 700,
      marginBottom: '1.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.4rem',
    },
    label: {
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      color: theme.textSecondary,
      letterSpacing: '0.5px',
      fontWeight: 600,
    },
    value: {
      fontSize: '1rem',
      fontWeight: 600,
      color: theme.text,
      wordBreak: 'break-word',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    legalDetail: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
    },
    legalValue: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: theme.primary,
      fontFamily: "'JetBrains Mono', 'SF Mono', 'Courier New', monospace",
      background: theme.accentBg,
      padding: '0.5rem 1rem',
      borderRadius: '8px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      border: `1px solid ${theme.border}`,
      wordBreak: 'break-all',
    },
    paymentRow: {
      background: theme.accentBg,
      borderRadius: '16px',
      padding: '1.25rem 1.5rem',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      border: `1px solid ${theme.border}`,
    },
    paymentItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.3rem',
    },
    paymentLabel: {
      fontSize: '0.7rem',
      fontWeight: 600,
      color: theme.textSecondary,
      textTransform: 'uppercase',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    paymentValue: {
      fontWeight: 700,
      color: theme.text,
      fontSize: '1rem',
    },
    paymentValueNotSet: {
      fontWeight: 500,
      color: theme.textSecondary,
      fontSize: '1rem',
      fontStyle: 'italic',
    },
    metaFooter: {
      marginTop: '1rem',
      fontSize: '0.75rem',
      color: theme.textSecondary,
      display: 'flex',
      gap: '1.5rem',
      flexWrap: 'wrap',
      paddingTop: '0.75rem',
      borderTop: `1px dashed ${theme.border}`,
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    }
  };

  return (
    <div style={styles.pageContainer}>
      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 600px) {
          .profile-header-fix {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }
          .wallet-min-fix {
            width: 100%;
            justify-content: space-between;
          }
          .section-fix {
            padding: 1.25rem 1.5rem;
          }
          .payment-row-fix {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>

      <div style={styles.card}>
        {/* ----- HEADER ----- */}
        <div style={styles.header} className="profile-header-fix">
          <div style={styles.nameContainer}>
            <h1 style={styles.restaurantName}>
              <Store size={28} /> {restaurantName}
            </h1>
            <span style={styles.badge}>
              <CheckCircle2 size={14} /> ACTIVE
            </span>
          </div>
          <div style={{...styles.walletMin}} className="wallet-min-fix">
            <span style={styles.walletItem}>
              <Wallet size={18} color={theme.primary} /> 
              Wallet: <span style={styles.amount}>₹{wallet}</span>
            </span>
            <span style={styles.walletItem}>
              <Receipt size={18} color={theme.primary} /> 
              Min Order: <span style={styles.amount}>₹{minOrder}</span>
            </span>
          </div>
        </div>

        {/* ----- CONTACT & INFO ----- */}
        <div style={styles.section} className="section-fix">
          <div style={styles.sectionTitle}>
            <User size={16} /> CONTACT & INFO
          </div>
          <div style={styles.infoGrid} className="info-grid-fix">
            <div style={styles.infoItem}>
              <span style={styles.label}>OWNER NAME</span>
              <span style={styles.value}><User size={16} color={theme.primary} /> {owner}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>PHONE</span>
              <span style={styles.value}><Phone size={16} color={theme.primary} /> {phone}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>EMAIL</span>
              <span style={styles.value}><Mail size={16} color={theme.primary} /> {email}</span>
            </div>
          </div>
        </div>

        {/* ----- LEGAL DETAILS ----- */}
        <div style={styles.section} className="section-fix">
          <div style={styles.sectionTitle}>
            <Award size={16} /> LEGAL DETAILS
          </div>
          <div style={styles.legalDetail}>
            <div style={styles.infoItem}>
              <span style={styles.label}>GST NUMBER (STIN)</span>
              <span style={styles.legalValue}>
                <FileText size={16} /> {stin}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>FSSAI LICENSE (ESS)</span>
              <span style={styles.legalValue}>
                <Award size={16} /> {ess}
              </span>
            </div>
          </div>
        </div>

        {/* ----- PAYMENT SETTINGS ----- */}
        <div style={{ ...styles.section, borderBottom: 'none' }} className="section-fix">
          <div style={styles.sectionTitle}>
            <CreditCard size={16} /> PAYMENT SETTINGS
          </div>
          <div style={styles.paymentRow} className="payment-row-fix">
            <div style={styles.paymentItem}>
              <span style={styles.paymentLabel}><Wallet size={14} /> PAY METHOD</span>
              <span style={isPaySet ? styles.paymentValue : styles.paymentValueNotSet}>
                {payDisplay}
              </span>
            </div>
            <div style={styles.paymentItem}>
              <span style={styles.paymentLabel}><User size={14} /> ACCOUNT HOLDER</span>
              <span style={styles.paymentValue}>{accountHolder}</span>
            </div>
            <div style={styles.metaItem}>
              <Hash size={14} color={theme.textSecondary} /> 
              <span style={{color: theme.textSecondary}}>ID: <strong style={{color: theme.text}}>{restaurantId}</strong></span>
            </div>
          </div>
          <div style={styles.metaFooter}>
            <span style={styles.metaItem}>
              <div style={{width: 6, height: 6, borderRadius: '50%', background: theme.successText}}></div> 
              Token: {userToken}
            </span>
            <span style={styles.metaItem}>
              <div style={{width: 6, height: 6, borderRadius: '50%', background: theme.primary}}></div> 
              Synced with localStorage
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantProfile;